import type { Dimension, Research } from '@/api/types';

import { ScoreValue, bandOf, formatScore } from '@/ui/ScoreValue';
import { retiredDimensions, scoredDimensions, type ScoredDimension } from './scoring';

function ScoreMeter({ dimension: d, value }: { dimension: Dimension; value: number | null }) {
  return (
    <div
      className={`meter score-meter${value === null ? '' : ` band-${bandOf(value)}`}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value ?? 0}
      aria-label={`${d.label} ${value === null ? 'not scored' : `${value} of 100`}, weight ${d.weight}%`}
      title={d.description}
    >
      <span className="meter-fill" style={{ width: `${value ?? 0}%` }} />
    </div>
  );
}

function ScoredRow({ row: { dimension: d, value, contribution, addedSince } }: { row: ScoredDimension }) {
  return (
    <div className="scorecard-row">
      <span className="scorecard-label" title={d.description || undefined}>
        {d.label}
      </span>
      <span className="scorecard-weight">{d.weight}%</span>
      {addedSince ? <span className="scorecard-note hint">Added after this research</span> : <ScoreMeter dimension={d} value={value} />}
      <span className="scorecard-value">{!addedSince && <ScoreValue value={value} />}</span>
      <span className="scorecard-contribution hint" title="Points this dimension adds to the total">
        {contribution === null ? '' : `+${formatScore(Math.round(contribution * 10) / 10)}`}
      </span>
    </div>
  );
}

function RetiredRow({ dimension: d, value }: { dimension: Dimension; value: number }) {
  return (
    <div className="scorecard-row is-retired">
      <span className="scorecard-label" title={d.description || undefined}>
        {d.label}
      </span>
      <span className="scorecard-weight">retired</span>
      <div
        className="meter score-meter"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-label={`${d.label} ${value} of 100, retired, not in the total`}
        title={d.description || undefined}
      >
        <span className="meter-fill" style={{ width: `${value}%` }} />
      </div>
      <span className="scorecard-value">
        <ScoreValue value={value} />
      </span>
      <span className="scorecard-contribution hint">not in total</span>
    </div>
  );
}

/** The run's dimension scores as a table: weight, the score as a bar and a number, and the points each adds to the total. */
export function Scorecard({ research, dimensions }: { research: Research; dimensions: Dimension[] }) {
  const retired = retiredDimensions(research, dimensions);
  const rows = scoredDimensions(research, dimensions);
  return (
    <section className="metric-tile usage-card idea-tile-dimensions" aria-label="Dimensions">
      <div className="scorecard">
        <div className="scorecard-row scorecard-head" aria-hidden="true">
          <span>Dimensions</span>
          <span className="scorecard-weight">Weight</span>
          <span className="scorecard-score-head">Score</span>
          <span className="scorecard-contribution" title="Points this dimension adds to the total: score × weight">
            Points
          </span>
        </div>
        {rows.map((row) => (
          <ScoredRow key={row.dimension.key} row={row} />
        ))}
        {retired.map((d) => (
          <RetiredRow key={d.key} dimension={d} value={research.scores[d.key]!} />
        ))}
      </div>
    </section>
  );
}
